import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Check,
  ChevronsUpDown,
  Plus,
  Settings as Cog,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import EnterpriseLockModal from "@/components/EnterpriseLockModal";

/** Strike 2 — sidebar workspace switcher (agency multi-tenant). */
export default function WorkspaceSwitcher() {
  const { workspaces, activeWorkspace, switchWorkspace, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const current = activeWorkspace;
  const brandColor = current?.branding?.primary_color || "#6366f1";

  const onNew = () => {
    setOpen(false);
    if (isAdmin) navigate("/workspace?new=1");
    else setLockOpen(true);
  };

  return (
    <div className="px-4 mt-3" ref={ref}>
      <div className="relative">
        <button
          onClick={() => setOpen((s) => !s)}
          data-testid="workspace-switcher-button"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-[#1f1f1f] bg-[#0d0d0d] hover:bg-[#141414] transition-colors"
        >
          <span
            className="w-5 h-5 rounded flex items-center justify-center shrink-0"
            style={{ backgroundColor: brandColor + "22", color: brandColor }}
          >
            <Building2 className="w-3.5 h-3.5" strokeWidth={1.8} />
          </span>
          <span
            className="flex-1 text-left text-sm truncate"
            data-testid="workspace-current-name"
          >
            {current?.name || "Workspace"}
          </span>
          <ChevronsUpDown
            className="w-3.5 h-3.5 text-neutral-500 shrink-0"
            strokeWidth={1.8}
          />
        </button>
        {open && (
          <div
            className="absolute left-0 right-0 top-full mt-1 bg-[#121212] border border-[#27272a] rounded-lg z-40 overflow-hidden"
            data-testid="workspace-dropdown"
          >
            <div className="max-h-56 overflow-y-auto py-1">
              {workspaces.map((w) => (
                <button
                  key={w.id}
                  onClick={() => {
                    setOpen(false);
                    if (w.id !== current?.id) switchWorkspace(w.id);
                  }}
                  data-testid={`workspace-option-${w.id}`}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#1a1a1a] text-left"
                >
                  <span className="flex-1 truncate">
                    {w.name}
                    {w.is_default ? (
                      <span className="ml-1.5 text-[9px] font-mono uppercase tracking-[0.15em] text-neutral-600">
                        default
                      </span>
                    ) : null}
                  </span>
                  {w.id === current?.id && (
                    <Check
                      className="w-3.5 h-3.5 text-emerald-400"
                      strokeWidth={2}
                    />
                  )}
                </button>
              ))}
            </div>
            <div className="border-t border-[#1f1f1f]">
              <button
                onClick={onNew}
                data-testid="workspace-new-button"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-300 hover:bg-[#1a1a1a]"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={1.8} /> New workspace
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/workspace");
                }}
                data-testid="workspace-settings-link"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-300 hover:bg-[#1a1a1a]"
              >
                <Cog className="w-3.5 h-3.5" strokeWidth={1.8} /> Workspace settings
              </button>
            </div>
          </div>
        )}
      </div>
      <EnterpriseLockModal open={lockOpen} onClose={() => setLockOpen(false)} />
    </div>
  );
}
